import { Intercom, trackEvent, update } from '@intercom/messenger-js-sdk';
import { useEffect, useRef } from 'react';

import { captureEvent } from '@/core/posthog-init';

const INTERCOM_APP_ID = 'geacv4jt';

export interface IntercomUser {
  email?: string;
  hash?: string;
  created_at?: number;
}

interface UseIntercomOptions {
  user?: IntercomUser;
  enabled?: boolean;
}

export function useIntercom({ user, enabled = true }: UseIntercomOptions) {
  const isBootedRef = useRef(false);
  const lastUserRef = useRef<string>('');

  // Initialize/update Intercom when enabled
  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Create a key to detect user data changes
    const userKey = JSON.stringify({ email: user?.email, hash: user?.hash });

    // Only update if user data changed
    if (lastUserRef.current === userKey && isBootedRef.current) {
      return;
    }

    const intercomSettings: {
      app_id: string;
      email?: string;
      user_id?: string;
    } = {
      app_id: INTERCOM_APP_ID,
    };

    // Add user identification if available
    if (user?.email) {
      intercomSettings.email = user.email;
    }
    if (user?.hash) {
      intercomSettings.user_id = `setup/${user.hash}`;
    } else if (user?.email) {
      intercomSettings.user_id = `setup/${user.email}`;
    }

    // Initialize Intercom (only once per session)
    if (!isBootedRef.current) {
      Intercom(intercomSettings);
      isBootedRef.current = true;
    } else {
      // Update if user data changed (e.g., email was added)
      update(intercomSettings);
    }

    lastUserRef.current = userKey;
  }, [user?.email, user?.hash, enabled]);

  return {
    /**
     * Track a custom event in Intercom
     * @param eventName - Name of the event
     * @param metadata - Optional metadata to attach to the event
     * @example
     * trackEvent('setup-started', { step: 'email' })
     * trackEvent('email-provided')
     */
    trackEvent: (eventName: string, metadata?: Record<string, unknown>) => {
      captureEvent(`setup.${eventName}`, metadata);
      if (enabled && isBootedRef.current) {
        trackEvent(eventName, metadata);
      }
    },
    /**
     * Check if Intercom is currently booted
     */
    isBooted: () => isBootedRef.current,
  };
}
